import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { catchError, map, merge, of, take, tap } from 'rxjs';

import { PlatformConfigService } from '../../../../features/config/config.service';
import { MonitoringService } from '../../../../features/monitoring/monitoring.service';
import { AuthService } from '../service/auth.service';

const DEFAULT_REDIRECT = 'https://evidentscientific.com';
const DEFAULT_METADATA = {
  errorCode: 'Not provided',
  errorMessage: 'No authentication error message available',
  correlationId: 'No correlation ID',
  subError: 'No sub-error information',
  name: 'No error name',
};

/**
 * Force redirect to logout url after MSAL failure
 *
 * @see : [MSAL lib does not handle apps with every routes protected by MSALGuard](
 *  https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/4614#issuecomment-1075668401
 * )
 *
 * This guard is a workaround to handle the case where the user is not authenticated after redirect;
 * and so force a redirect to external public URI.
 * - use config : `auth.redirects.fail | uris.logout | 'https://evidentscientific.com'`
 *
 * ### Many kind of failures:
 *
 * Cancelling any flow = auth fail
 * - wait one tick so auth_service may catch specific failures
 * - eg: cancel reset_password = fail login
 *   + so will reload to retry login - we did not trully fail a login here
 *
 * @see AUTH_FAIL_URI env variable to match route path with this guard
 */
export const AuthFailureGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const platformConfigService = inject(PlatformConfigService);
  const monitoringService = inject(MonitoringService);
  const authService = inject(AuthService);

  return merge(
    authService.authError$,
    authService
      .getMsalService()!
      .handleRedirectObservable()
      .pipe(
        map(() => {
          throw new Error('unknown');
        }),
        catchError((error) => of(error)),
      ),
  ).pipe(
    take(1),
    tap((error) => handleRedirect(route, platformConfigService, monitoringService, error)),
    map(() => false),
  );
};

function handleRedirect(
  route: ActivatedRouteSnapshot,
  configService: PlatformConfigService,
  monitoringService: MonitoringService,
  authError: unknown = null,
): void {
  const redirectUrl = getRedirectUrl(route, configService);
  const failCode = (authError as string) || 'unknown';
  const redirectUrlWithParams = buildRedirectUrl(redirectUrl, failCode);

  logAndTrackError(monitoringService, failCode, authError, redirectUrlWithParams);
  window.location.href = redirectUrlWithParams;
}

function getRedirectUrl(
  route: ActivatedRouteSnapshot,
  configService: PlatformConfigService,
): string {
  return (
    route.data.redirect ??
    configService.get('auth')?.redirects.logout ??
    configService.get('uris')?.logout ??
    DEFAULT_REDIRECT
  );
}

function buildRedirectUrl(baseUrl: string, failCode: string): string {
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('authFailCode', encodeURIComponent(failCode));

  return url.toString();
}

function logAndTrackError(
  monitoringService: MonitoringService,
  failCode: string,
  authError: unknown | null,
  redirectUrl: string,
): void {
  const context = 'auth.aadb2c.fail';
  const isUnknown = failCode === 'unknown';
  let errorDetails = `: ${failCode} - No authentication error details available`;
  let errorMeta = DEFAULT_METADATA;

  if (typeof authError === 'object' && authError !== null) {
    const err = authError as { errorMessage?: string };
    errorDetails = `: ${failCode}${err.errorMessage ? ` - ${err.errorMessage}` : ''}`;
    errorMeta = { ...DEFAULT_METADATA, ...authError };
  }

  const error = new Error(`AADB2C Auth Failure${errorDetails}`);
  console.warn(
    `AuthFailureGuard - redirect to ${redirectUrl}${isUnknown ? ' (unknown error)' : ''}`,
  );

  monitoringService.trackError(error, context, {
    failCode,
    redirectUrl,
    ...errorMeta,
  });
}
